import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ConfigItemRow {
  readonly ciId: string;
  readonly ciCode: string;
  readonly ciName: string;
  readonly ciType: string;
  readonly status: string;
  readonly currentVersion: string | null;
  readonly environmentId: string | null;
}

const TYPES = ['', 'APPLICATION_COMPONENT', 'DATABASE', 'INTERFACE', 'API', 'SERVICE', 'CONFIGURATION', 'DOCUMENT', 'DEPLOYMENT_PACKAGE'];
const STATUSES = ['', 'ACTIVE', 'INACTIVE', 'RETIRED'];
const CREATE_TYPES = TYPES.filter(Boolean);
const SET_STATUSES = ['ACTIVE', 'INACTIVE', 'RETIRED'];

/**
 * M32 — Configuration Management Console (§31 / §21-22). The CMDB register of
 * configuration items (T426) with type, lifecycle status and current version
 * pointer into the versioned config store (T427). Supports registering a CI and
 * transitioning its lifecycle status, each wired to the guarded
 * /v1/devops/configuration-items write API. Filterable by CI type and status;
 * underpins the BR-04 config-drift and rollback-to-version workflows.
 */
export function ConfigurationManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ConfigItemRow[]>([]);
  const [ciType, setCiType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('APPLICATION_COMPONENT');
  const [newVersion, setNewVersion] = useState('');
  const [setStatusValue, setSetStatusValue] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (ciType) params.set('ciType', ciType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ConfigItemRow[] }>(`/v1/devops/configuration-items${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, ciType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.config.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m32.config.nameRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/configuration-items`, {
        method: 'POST',
        body: { ciName: newName.trim(), ciType: newType, currentVersion: newVersion.trim() || undefined },
        token,
      });
      setNotice(tr('m32.config.registeredNotice', { name: newName.trim() }));
      setNewName('');
      setNewVersion('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatusFor = async (r: ConfigItemRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/configuration-items/${r.ciId}/status`, {
        method: 'POST',
        body: { status: setStatusValue[r.ciId] ?? 'ACTIVE' },
        token,
      });
      setNotice(tr('m32.config.statusUpdated', { code: r.ciCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="configuration-management">
      <h1>{tr('m32.config.title')}</h1>

      <section aria-label="create-ci">
        <h2>{tr('m32.config.registerTitle')}</h2>
        <form aria-label="ci-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.config.fieldName')}<input aria-label="field-ci-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{tr('m32.config.type')}
            <select aria-label="field-ci-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m32.config.fieldVersion')}<input aria-label="field-current-version" value={newVersion} onChange={(e) => setNewVersion(e.target.value)} /></label>
          <button type="submit" aria-label="submit-ci" disabled={busy}>{busy ? tr('m32.config.registering') : tr('m32.config.registerBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="ci-error">{actionError}</p>}
        {notice && <p aria-label="ci-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.config.type')}
          <select aria-label="filter-ci-type" value={ciType} onChange={(e) => setCiType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.config.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.config.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.config.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.config.loading')}</p> : (
        <>
          <p aria-label="ci-count">{tr('m32.config.count', { count: rows.length })}</p>
          <table aria-label="ci-table">
            <thead><tr><th>{tr('m32.config.thCode')}</th><th>{tr('m32.config.fieldName')}</th><th>{tr('m32.config.type')}</th><th>{tr('m32.config.fieldVersion')}</th><th>{tr('m32.config.status')}</th><th>{tr('m32.config.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ciId} data-status={r.status}>
                  <td>{r.ciCode}</td><td>{r.ciName}</td><td>{r.ciType}</td>
                  <td>{r.currentVersion ?? '—'}</td><td>{r.status}</td>
                  <td>
                    <select aria-label={`status-select-${r.ciCode}`} value={setStatusValue[r.ciId] ?? 'ACTIVE'} onChange={(e) => setSetStatusValue((m) => ({ ...m, [r.ciId]: e.target.value }))}>
                      {SET_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button type="button" aria-label={`set-status-${r.ciCode}`} onClick={() => void setStatusFor(r)}>{tr('m32.config.setStatus')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
